import {
  formatCriteria,
  getUnitLabel,
  mapTargetUnitToCriteriaUnit,
  parseCriteria,
  validateCriteriaFormat,
  type CriteriaOperator,
  type CriteriaUnit,
} from "@/modules/kpi/presentation/utils/criteria-validator";
import {
  Field,
  Input,
  Label,
  RHF,
  ToggleGroup,
  ToggleGroupItem,
} from "@devhop/ui";
import { AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import * as React from "react";

export interface FormCriteriaInputProps {
  name: string;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  requiredMark?: boolean;
  targetUnit?: "percent" | "count" | "score" | "currency" | "none" | null;
  placeholder?: string;
  className?: string;
}

export function FormCriteriaInput({
  name,
  label,
  hint,
  requiredMark,
  targetUnit,
  placeholder,
  className,
}: FormCriteriaInputProps) {
  const { Controller, useFormContext, useWatch } = RHF;
  const { control } = useFormContext();
  const formValue = useWatch({ control, name: name as never }) as unknown as
    | string
    | null
    | undefined;

  // Parse existing value - always use standard mode
  const parsed = React.useMemo(
    () => parseCriteria(formValue as string | null),
    [formValue],
  );

  // Standard mode state - initialize from parsed value
  const [operator, setOperator] = React.useState<CriteriaOperator | null>(
    parsed.operator || ">=",
  );
  const [value, setValue] = React.useState<string>(
    parsed.value !== null ? String(parsed.value) : "",
  );

  // Unit is determined by targetUnit - if targetUnit exists, use it; otherwise use parsed unit or default
  const unit: CriteriaUnit | null = React.useMemo(() => {
    if (targetUnit) {
      const mappedUnit = mapTargetUnitToCriteriaUnit(targetUnit);
      if (mappedUnit) return mappedUnit;
    }
    // If no targetUnit, use parsed unit or default to %
    return parsed.unit || "%";
  }, [targetUnit, parsed.unit]);

  // Sync internal state when form value changes externally
  React.useEffect(() => {
    const currentParsed = parseCriteria(formValue as string | null);
    // Only sync if it's standard format
    if (!currentParsed.isCustom) {
      if (currentParsed.operator && currentParsed.operator !== operator) {
        setOperator(currentParsed.operator);
      }
      if (
        currentParsed.value !== null &&
        String(currentParsed.value) !== value
      ) {
        setValue(String(currentParsed.value));
      }
    }
  }, [formValue, operator, value]);

  // Validation state - always standard mode
  const validation = React.useMemo(() => {
    const formatted = formatCriteria(
      operator,
      value ? Number.parseFloat(value) : null,
      unit,
    );
    return validateCriteriaFormat(formatted, targetUnit ?? undefined);
  }, [operator, value, unit, targetUnit]);

  return (
    <Field name={name} label={label} hint={hint} requiredMark={requiredMark}>
      <Controller
        control={control}
        name={name as never}
        render={({ field }) => {
          // Helper function to update form value in standard mode
          const updateStandardModeValue = (
            newOperator: CriteriaOperator | null,
            newValue: string,
            newUnit: CriteriaUnit | null,
          ) => {
            if (newOperator) {
              const numValue = newValue ? Number.parseFloat(newValue) : null;
              const formatted = formatCriteria(
                newOperator,
                numValue,
                newUnit || "",
              );
              field.onChange(formatted || null);
            } else {
              field.onChange(null);
            }
          };

          return (
            <div className={`space-y-3 ${className || ""}`}>
              {/* Standard Mode: Operator + Value + Unit */}
              <div className="space-y-3">
                {/* Inline Row: Operator + Value + Unit */}
                <div className="flex flex-wrap items-end gap-4">
                  {/* Operator */}
                  <div className="min-w-[200px] flex-1 space-y-2">
                    <Label className="text-xs">ໂອເປຣເຕີ</Label>
                    <ToggleGroup
                      type="single"
                      value={operator || ""}
                      onValueChange={(val: string) => {
                        if (val) {
                          const op = val as CriteriaOperator;
                          setOperator(op);
                          updateStandardModeValue(op, value, unit);
                        }
                      }}
                      className="flex flex-wrap gap-2"
                    >
                      <ToggleGroupItem
                        value=">="
                        aria-label="Greater than or equal"
                        className="px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                      >
                        ≥
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value=">"
                        aria-label="Greater than"
                        className="px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                      >
                        &gt;
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="<="
                        aria-label="Less than or equal"
                        className="px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                      >
                        ≤
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="<"
                        aria-label="Less than"
                        className="px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                      >
                        &lt;
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="="
                        aria-label="Equal"
                        className="px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                      >
                        =
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>

                  {/* Value */}
                  <div className="min-w-[120px] flex-1 space-y-2">
                    <Label className="text-xs">ຄ່າ</Label>
                    <Input
                      type="number"
                      step="any"
                      value={value}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setValue(newValue);
                        updateStandardModeValue(operator, newValue, unit);
                      }}
                      placeholder={placeholder || "90"}
                      className={
                        validation.isValid
                          ? validation.error
                            ? "border-destructive focus-visible:ring-destructive"
                            : "border-input"
                          : "border-destructive focus-visible:ring-destructive"
                      }
                    />
                  </div>

                  {/* Unit - Display only (derived from targetUnit) */}
                  <div className="min-w-[120px] flex-1 space-y-2">
                    <Label className="text-xs">ຫົວໜ່ວຍ</Label>
                    <div className="rounded-md border bg-muted px-3 py-2">
                      <p className="font-normal text-sm">
                        {unit !== null ? getUnitLabel(unit) : "ບໍ່ມີຫົວໜ່ວຍ"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Format Preview */}
                {operator && value && (
                  <div className="rounded-md border bg-muted/50 p-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Label className="whitespace-nowrap text-muted-foreground text-xs">
                          ຮູບແບບທີ່ຈະບັນທຶກ:
                        </Label>
                        <p className="font-mono text-sm">
                          {formatCriteria(
                            operator,
                            Number.parseFloat(value),
                            unit || "",
                          ) || "(ກຳລັງກຳນົດ...)"}
                        </p>
                      </div>
                      {validation.isValid && !validation.error && (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                      )}
                      {validation.error && (
                        <XCircle className="h-4 w-4 shrink-0 text-destructive" />
                      )}
                      {validation.warning && (
                        <AlertCircle className="h-4 w-4 shrink-0 text-yellow-600" />
                      )}
                    </div>
                  </div>
                )}

                {/* Validation Messages */}
                {validation.error && (
                  <p className="flex items-center gap-1 text-destructive text-xs">
                    <XCircle className="h-3 w-3" />
                    {validation.error}
                  </p>
                )}
                {validation.warning && (
                  <p className="flex items-center gap-1 text-xs text-yellow-600">
                    <AlertCircle className="h-3 w-3" />
                    {validation.warning}
                  </p>
                )}
                {/* {validation.isValid &&
                  !validation.error &&
                  operator &&
                  value && (
                    <p className="flex items-center gap-1 text-green-600 text-xs">
                      <CheckCircle2 className="h-3 w-3" />
                      ຮູບແບບຖືກຕ້ອງ
                    </p>
                  )} */}
              </div>
            </div>
          );
        }}
      />
    </Field>
  );
}
